import { createContext, useContext, useState, useCallback, useMemo, ReactNode, useEffect } from 'react';

export interface MasterCartItem {
  itemId: string;
  productName: string;
  supplierName: string;
  supplierCode: string; // 'MKR' | 'JMB' | 'PNP' | 'SHP' | 'BXR'
  unitPrice: number;
  wasPrice?: number;
  savingPerUnit: number;
  quantity: number;
}

export interface SupplierGroup {
  supplierName: string;
  supplierCode: string;
  items: MasterCartItem[];
  subtotal: number;
  savings: number;
}

interface MasterCartContextType {
  items: MasterCartItem[];
  addItem: (item: Omit<MasterCartItem, 'quantity'> & { quantity?: number }) => void;
  removeItem: (itemId: string) => void;
  updateQuantity: (itemId: string, quantity: number) => void;
  clearCart: () => void;
  hasItem: (itemId: string) => boolean;
  itemCount: number;
  grandTotal: number;
  totalSavings: number;
  groups: SupplierGroup[];
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

const MasterCartContext = createContext<MasterCartContextType | undefined>(undefined);

const STORAGE_KEY = 'sis-thoko-master-cart-v1';

export function MasterCartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<MasterCartItem[]>(() => {
    try {
      const raw = typeof window !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch {}
  }, [items]);

  const addItem = useCallback((item: Omit<MasterCartItem, 'quantity'> & { quantity?: number }) => {
    setItems(prev => {
      const existing = prev.find(i => i.itemId === item.itemId);
      if (existing) {
        return prev.map(i => i.itemId === item.itemId ? { ...i, quantity: i.quantity + (item.quantity || 1) } : i);
      }
      return [...prev, { ...item, quantity: item.quantity || 1 }];
    });
  }, []);

  const removeItem = useCallback((itemId: string) => {
    setItems(prev => prev.filter(i => i.itemId !== itemId));
  }, []);

  const updateQuantity = useCallback((itemId: string, quantity: number) => {
    if (quantity <= 0) setItems(prev => prev.filter(i => i.itemId !== itemId));
    else setItems(prev => prev.map(i => i.itemId === itemId ? { ...i, quantity } : i));
  }, []);

  const clearCart = useCallback(() => setItems([]), []);
  const hasItem = useCallback((itemId: string) => items.some(i => i.itemId === itemId), [items]);

  const { itemCount, grandTotal, totalSavings, groups } = useMemo(() => {
    const map = new Map<string, SupplierGroup>();
    let count = 0, total = 0, save = 0;
    for (const i of items) {
      count += i.quantity;
      total += i.unitPrice * i.quantity;
      save += i.savingPerUnit * i.quantity;
      const key = i.supplierCode || i.supplierName;
      if (!map.has(key)) {
        map.set(key, { supplierName: i.supplierName, supplierCode: i.supplierCode, items: [], subtotal: 0, savings: 0 });
      }
      const g = map.get(key)!;
      g.items.push(i);
      g.subtotal += i.unitPrice * i.quantity;
      g.savings += i.savingPerUnit * i.quantity;
    }
    return { itemCount: count, grandTotal: total, totalSavings: save, groups: Array.from(map.values()) };
  }, [items]);

  return (
    <MasterCartContext.Provider value={{ items, addItem, removeItem, updateQuantity, clearCart, hasItem, itemCount, grandTotal, totalSavings, groups, isOpen, setIsOpen }}>
      {children}
    </MasterCartContext.Provider>
  );
}

export function useMasterCart() {
  const ctx = useContext(MasterCartContext);
  if (!ctx) throw new Error('useMasterCart must be used within MasterCartProvider');
  return ctx;
}

export const SUPPLIER_CODE_MAP: Record<string, string> = {
  'Makro': 'MKR',
  'Jumbo': 'JMB',
  'PnP Wholesale': 'PNP',
  'Shoprite': 'SHP',
  'Boxer': 'BXR',
};

// API-integrated suppliers (rest are 'manual')
export const API_SUPPLIERS = new Set<string>(['MKR', 'PNP']);

export function integrationFor(code: string): 'api' | 'manual' {
  return API_SUPPLIERS.has(code) ? 'api' : 'manual';
}
